import { useEffect } from 'react';
import { useReduxState } from 'hooks';
import { CheckboxInput } from 'components/common/form/common';

import BoxTableCell from './BoxTableCell';
import { useBoxContext, useBoxDispatch } from './BoxContext';

type CheckboxProps =
  | { withCheckbox?: false; withCheckboxIdentifier?: never }
  | { withCheckbox: true; withCheckboxIdentifier: string };

interface Props {
  children: React.ReactNode;
  hideAtBreakpoint?: number;
  sticky?: boolean;
  style?: React.CSSProperties;
  verticalAlign?: 'top' | 'bottom' | 'middle';
}

const BoxTableHeader = ({
  children,
  hideAtBreakpoint,
  style,
  verticalAlign = 'middle',
  withCheckboxIdentifier,
  withCheckbox = false
}: Props & CheckboxProps) => {
  const state = useBoxContext();

  const dispatch = useBoxDispatch();

  const { theme } = useReduxState();

  useEffect(() => {
    dispatch({ type: 'clearVisibleItems' });
    dispatch({
      type: 'configure',
      payload: {
        active: withCheckbox,
        identifier: withCheckboxIdentifier || ''
      }
    });
  }, []);

  const handleSelect = (ev: React.FormEvent<HTMLElement>) => {
    const value = (ev.target as HTMLInputElement).checked;
    dispatch({ type: value ? 'selectAll' : 'deselectAll' });
  };

  const allChecked =
    state.visibleItems.length &&
    state.visibleItems.every((id) => state.selectedItems?.[id]);

  const someChecked =
    state.visibleItems.length &&
    state.visibleItems.some((id) => state.selectedItems?.[id]);

  return (
    <div
      className={'box-table-header' + `${hideAtBreakpoint ? ' hide' : ''}`}
      style={style}
    >
      {withCheckbox ? (
        <BoxTableCell
          sticky={true}
          stickyOffset={-48}
          shrink={true}
          verticalAlign={verticalAlign}
        >
          <CheckboxInput
            id={`select-all-in-box-table-header-${withCheckboxIdentifier}`}
            indeterminate={someChecked && !allChecked}
            onChange={handleSelect}
            stopPropagation={true}
            {...{ checked: someChecked }}
          />
        </BoxTableCell>
      ) : null}

      {children}

      <style jsx>{`
        .box-table-header {
          background-color: ${theme.lightGreyLighter};
          border-bottom: 1px solid ${theme.lightGreyDark};
          display: table-row;
          white-space: nowrap;
        }
        .hide {
          display: none;
        }
        .box-table-header :global(.box-table-cell) {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 700;
        }
        @media (min-width: ${hideAtBreakpoint}px) {
          .hide {
            display: table-row;
          }
        }
      `}</style>
    </div>
  );
};

export default BoxTableHeader;
